/**
 * EditMailboxPanel Component
 * Slide-over panel for editing mailbox settings
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  EnvelopeIcon,
  UserIcon,
  ServerIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  useMailbox,
  useUpdateMailbox,
  useSuspendMailbox,
  useReactivateMailbox,
  useUpdateMailboxQuota,
  useSendIdentities,
  useCreateSendIdentity,
  useVerifySendIdentity,
  useUpdateSendIdentity,
  useDeleteSendIdentity,
} from '../../hooks/useMailboxes';
import { useFeatureFlag } from '../../context/TenantContext';
import type { Mailbox, UpdateMailboxRequest } from '../../types/admin.types';

// Format bytes to human-readable
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Quota presets in bytes
const QUOTA_PRESETS = [
  { label: '1 GB', value: 1 * 1024 * 1024 * 1024 },
  { label: '2 GB', value: 2 * 1024 * 1024 * 1024 },
  { label: '5 GB', value: 5 * 1024 * 1024 * 1024 },
  { label: '10 GB', value: 10 * 1024 * 1024 * 1024 },
  { label: '25 GB', value: 25 * 1024 * 1024 * 1024 },
  { label: '50 GB', value: 50 * 1024 * 1024 * 1024 },
];

type TabId = 'general' | 'quota' | 'forwarding' | 'sendIdentities' | 'autoreply';

interface EditMailboxPanelProps {
  mailbox: Mailbox | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function EditMailboxPanel({
  mailbox: initialMailbox,
  isOpen,
  onClose,
  onUpdate,
}: EditMailboxPanelProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const sendIdentitiesEnabled = useFeatureFlag('send_identities');

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [quotaBytes, setQuotaBytes] = useState(0);
  const [forwardTo, setForwardTo] = useState('');
  const [forwardKeepCopy, setForwardKeepCopy] = useState(true);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplySubject, setAutoReplySubject] = useState('');
  const [autoReplyBody, setAutoReplyBody] = useState('');
  const [identityEmail, setIdentityEmail] = useState('');
  const [identityDisplayName, setIdentityDisplayName] = useState('');
  const [identityProviderName, setIdentityProviderName] = useState('External SMTP');
  const [identitySmtpHost, setIdentitySmtpHost] = useState('');
  const [identitySmtpPort, setIdentitySmtpPort] = useState(587);
  const [identitySmtpSecure, setIdentitySmtpSecure] = useState(false);
  const [identityUsername, setIdentityUsername] = useState('');
  const [identityPassword, setIdentityPassword] = useState('');

  // Confirmation dialogs
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch fresh mailbox data
  const { data: mailbox, refetch } = useMailbox(initialMailbox?.mailbox_id || '');

  // Mutations
  const updateMutation = useUpdateMailbox();
  const suspendMutation = useSuspendMailbox();
  const reactivateMutation = useReactivateMailbox();
  const quotaMutation = useUpdateMailboxQuota();
  const { data: sendIdentities = [], refetch: refetchSendIdentities } = useSendIdentities(
    initialMailbox?.mailbox_id || '',
    sendIdentitiesEnabled,
  );
  const createIdentityMutation = useCreateSendIdentity();
  const verifyIdentityMutation = useVerifySendIdentity();
  const updateIdentityMutation = useUpdateSendIdentity();
  const deleteIdentityMutation = useDeleteSendIdentity();

  // Initialize form from mailbox data
  useEffect(() => {
    if (mailbox) {
      setDisplayName(mailbox.display_name || '');
      setQuotaBytes(mailbox.quota_bytes);
      setForwardTo(mailbox.forward_to || '');
      setForwardKeepCopy(mailbox.forward_keep_copy);
      setAutoReplyEnabled(mailbox.auto_reply_enabled);
      setAutoReplySubject(mailbox.auto_reply_subject || '');
      setAutoReplyBody(mailbox.auto_reply_body || '');
    }
  }, [mailbox]);

  useEffect(() => {
    if (!sendIdentitiesEnabled && activeTab === 'sendIdentities') {
      setActiveTab('general');
    }
  }, [activeTab, sendIdentitiesEnabled]);

  // Tab definitions
  const tabs: { id: TabId; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'quota', label: 'Storage' },
    { id: 'forwarding', label: 'Forwarding' },
    ...(sendIdentitiesEnabled ? [{ id: 'sendIdentities' as const, label: 'Send Identities' }] : []),
    { id: 'autoreply', label: 'Auto-Reply' },
  ];

  // Handlers
  const handleSaveGeneral = useCallback(async () => {
    if (!mailbox) return;
    await updateMutation.mutateAsync({
      mailboxId: mailbox.mailbox_id,
      data: { display_name: displayName },
    });
    onUpdate?.();
  }, [mailbox, displayName, updateMutation, onUpdate]);

  const handleSaveQuota = useCallback(async () => {
    if (!mailbox) return;
    await quotaMutation.mutateAsync({
      mailboxId: mailbox.mailbox_id,
      quotaBytes: quotaBytes,
    });
    onUpdate?.();
  }, [mailbox, quotaBytes, quotaMutation, onUpdate]);

  const handleSaveForwarding = useCallback(async () => {
    if (!mailbox) return;
    const data: UpdateMailboxRequest = {
      forward_to: forwardTo || null,
      forward_keep_copy: forwardKeepCopy,
    };
    await updateMutation.mutateAsync({
      mailboxId: mailbox.mailbox_id,
      data,
    });
    onUpdate?.();
  }, [mailbox, forwardTo, forwardKeepCopy, updateMutation, onUpdate]);

  const handleSaveAutoReply = useCallback(async () => {
    if (!mailbox) return;
    const data: UpdateMailboxRequest = {
      auto_reply_enabled: autoReplyEnabled,
      auto_reply_subject: autoReplySubject || undefined,
      auto_reply_body: autoReplyBody || undefined,
    };
    await updateMutation.mutateAsync({
      mailboxId: mailbox.mailbox_id,
      data,
    });
    onUpdate?.();
  }, [mailbox, autoReplyEnabled, autoReplySubject, autoReplyBody, updateMutation, onUpdate]);

  const handleCreateIdentity = useCallback(async () => {
    if (!mailbox) return;
    await createIdentityMutation.mutateAsync({
      mailboxId: mailbox.mailbox_id,
      data: {
        email: identityEmail,
        displayName: identityDisplayName || undefined,
        providerName: identityProviderName || 'External SMTP',
        smtpHost: identitySmtpHost,
        smtpPort: identitySmtpPort,
        smtpSecure: identitySmtpSecure,
        smtpRequireTls: !identitySmtpSecure,
        username: identityUsername || identityEmail,
        password: identityPassword,
        verifyNow: true,
      },
    });
    setIdentityEmail('');
    setIdentityDisplayName('');
    setIdentityProviderName('External SMTP');
    setIdentitySmtpHost('');
    setIdentitySmtpPort(587);
    setIdentitySmtpSecure(false);
    setIdentityUsername('');
    setIdentityPassword('');
    await refetchSendIdentities();
  }, [
    mailbox,
    createIdentityMutation,
    identityEmail,
    identityDisplayName,
    identityProviderName,
    identitySmtpHost,
    identitySmtpPort,
    identitySmtpSecure,
    identityUsername,
    identityPassword,
    refetchSendIdentities,
  ]);

  const handleSuspend = useCallback(async () => {
    if (!mailbox) return;
    await suspendMutation.mutateAsync(mailbox.mailbox_id);
    setShowSuspendConfirm(false);
    refetch();
    onUpdate?.();
  }, [mailbox, suspendMutation, refetch, onUpdate]);

  const handleReactivate = useCallback(async () => {
    if (!mailbox) return;
    await reactivateMutation.mutateAsync(mailbox.mailbox_id);
    refetch();
    onUpdate?.();
  }, [mailbox, reactivateMutation, refetch, onUpdate]);

  const handleClose = useCallback(() => {
    setActiveTab('general');
    setShowSuspendConfirm(false);
    setShowDeleteConfirm(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const currentMailbox = mailbox || initialMailbox;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-lg">
          <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-lex-accent-soft rounded-full flex items-center justify-center">
                    <EnvelopeIcon className="h-5 w-5 text-lex-accent" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {currentMailbox?.email}
                    </h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      {currentMailbox?.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircleIcon className="h-3 w-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                          <PauseCircleIcon className="h-3 w-3" />
                          Suspended
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex -mb-px px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-lex-accent text-lex-accent'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Info */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {currentMailbox?.email}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                      <span className="text-gray-900 dark:text-white">
                        {currentMailbox?.created_at
                          ? new Date(currentMailbox.created_at).toLocaleDateString()
                          : '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Last Login</span>
                      <span className="text-gray-900 dark:text-white">
                        {currentMailbox?.last_login_at
                          ? new Date(currentMailbox.last_login_at).toLocaleDateString()
                          : 'Never'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleSaveGeneral}
                    disabled={updateMutation.isPending}
                    className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}

              {/* Quota Tab */}
              {activeTab === 'quota' && (
                <div className="space-y-6">
                  {/* Current Usage */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Usage
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600 dark:text-gray-400">
                          {formatBytes(currentMailbox?.used_bytes || 0)} used
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {formatBytes(currentMailbox?.quota_bytes || 0)} total
                        </span>
                      </div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-lex-accent transition-all"
                          style={{
                            width: `${
                              currentMailbox?.quota_bytes
                                ? Math.min(
                                    ((currentMailbox?.used_bytes || 0) /
                                      currentMailbox.quota_bytes) *
                                      100,
                                    100
                                  )
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quota Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Storage Quota
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {QUOTA_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          onClick={() => setQuotaBytes(preset.value)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            quotaBytes === preset.value
                              ? 'bg-lex-accent text-lex-accent-contrast'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleSaveQuota}
                    disabled={quotaMutation.isPending}
                    className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
                  >
                    {quotaMutation.isPending ? 'Saving...' : 'Update Quota'}
                  </button>
                </div>
              )}

              {/* Forwarding Tab */}
              {activeTab === 'forwarding' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Forward to Address
                    </label>
                    <input
                      type="email"
                      value={forwardTo}
                      onChange={(e) => setForwardTo(e.target.value)}
                      placeholder="external@example.com"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Leave empty to disable forwarding
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="keepCopy"
                      checked={forwardKeepCopy}
                      onChange={(e) => setForwardKeepCopy(e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                    />
                    <label
                      htmlFor="keepCopy"
                      className="text-sm text-gray-700 dark:text-gray-300"
                    >
                      Keep a copy of forwarded messages
                    </label>
                  </div>

                  <button
                    onClick={handleSaveForwarding}
                    disabled={updateMutation.isPending}
                    className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Forwarding Settings'}
                  </button>
                </div>
              )}

              {/* Auto-Reply Tab */}
              {activeTab === 'sendIdentities' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Verified sender addresses
                    </h3>
                    <div className="space-y-2">
                      {sendIdentities.map((identity) => (
                        <div
                          key={identity.identityId}
                          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white truncate">
                                {identity.displayName ? `${identity.displayName} <${identity.email}>` : identity.email}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {identity.providerName} · {identity.status}
                                {identity.isDefault ? ' · Default' : ''}
                              </div>
                              {identity.lastError && (
                                <div className="mt-1 text-xs text-red-500 dark:text-red-400">
                                  {identity.lastError}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {identity.identityType !== 'local_mailbox' && (
                                <button
                                  onClick={() => verifyIdentityMutation.mutate({ mailboxId: identity.mailboxId, identityId: identity.identityId })}
                                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  Verify
                                </button>
                              )}
                              {!identity.isDefault && identity.status === 'verified' && (
                                <button
                                  onClick={() => updateIdentityMutation.mutate({ mailboxId: identity.mailboxId, identityId: identity.identityId, data: { isDefault: true } })}
                                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  Default
                                </button>
                              )}
                              {identity.identityType !== 'local_mailbox' && (
                                <button
                                  onClick={() => deleteIdentityMutation.mutate({ mailboxId: identity.mailboxId, identityId: identity.identityId })}
                                  className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  title="Disable identity"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <PlusIcon className="h-4 w-4 text-lex-accent" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Add external SMTP identity
                      </h3>
                    </div>
                    <input
                      type="email"
                      value={identityEmail}
                      onChange={(e) => {
                        setIdentityEmail(e.target.value);
                        if (!identityUsername) setIdentityUsername(e.target.value);
                      }}
                      placeholder="attorney@firm.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <input
                      type="text"
                      value={identityDisplayName}
                      onChange={(e) => setIdentityDisplayName(e.target.value)}
                      placeholder="Display name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <input
                      type="text"
                      value={identityProviderName}
                      onChange={(e) => setIdentityProviderName(e.target.value)}
                      placeholder="Provider name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <div className="grid grid-cols-[1fr_88px] gap-2">
                      <input
                        type="text"
                        value={identitySmtpHost}
                        onChange={(e) => setIdentitySmtpHost(e.target.value)}
                        placeholder="smtp.provider.com"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                      <input
                        type="number"
                        value={identitySmtpPort}
                        onChange={(e) => setIdentitySmtpPort(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={identitySmtpSecure}
                        onChange={(e) => setIdentitySmtpSecure(e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                      />
                      Use implicit TLS / port 465
                    </label>
                    <input
                      type="email"
                      value={identityUsername}
                      onChange={(e) => setIdentityUsername(e.target.value)}
                      placeholder="SMTP username"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <input
                      type="password"
                      value={identityPassword}
                      onChange={(e) => setIdentityPassword(e.target.value)}
                      placeholder="SMTP password or app password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <button
                      onClick={handleCreateIdentity}
                      disabled={
                        createIdentityMutation.isPending ||
                        !identityEmail ||
                        !identitySmtpHost ||
                        !identityUsername ||
                        !identityPassword
                      }
                      className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
                    >
                      {createIdentityMutation.isPending ? 'Verifying...' : 'Add and verify identity'}
                    </button>
                  </div>
                </div>
              )}

              {/* Auto-Reply Tab */}
              {activeTab === 'autoreply' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="autoReply"
                      checked={autoReplyEnabled}
                      onChange={(e) => setAutoReplyEnabled(e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                    />
                    <label
                      htmlFor="autoReply"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Enable automatic replies
                    </label>
                  </div>

                  {autoReplyEnabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          value={autoReplySubject}
                          onChange={(e) => setAutoReplySubject(e.target.value)}
                          placeholder="Out of Office"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Message
                        </label>
                        <textarea
                          value={autoReplyBody}
                          onChange={(e) => setAutoReplyBody(e.target.value)}
                          placeholder="I am currently out of the office..."
                          rows={5}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                        />
                      </div>
                    </>
                  )}

                  <button
                    onClick={handleSaveAutoReply}
                    disabled={updateMutation.isPending}
                    className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Auto-Reply Settings'}
                  </button>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                {currentMailbox?.is_active ? (
                  <button
                    onClick={() => setShowSuspendConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                  >
                    <PauseCircleIcon className="h-5 w-5" />
                    Suspend Mailbox
                  </button>
                ) : (
                  <button
                    onClick={handleReactivate}
                    disabled={reactivateMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                    {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Suspend Confirmation Modal */}
      {showSuspendConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <ExclamationCircleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Suspend Mailbox?
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This will prevent the user from sending or receiving emails. The mailbox data will be
              preserved and can be reactivated later.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSuspendConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspend}
                disabled={suspendMutation.isPending}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
              >
                {suspendMutation.isPending ? 'Suspending...' : 'Suspend Mailbox'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditMailboxPanel;
